import React, { useState } from 'react';
import { 
  Users, UserPlus, FileSpreadsheet, Download, Upload, 
  CheckCircle, Search, Filter, Trash2, Eye, Shield, FileText 
} from 'lucide-react';

interface Student {
  id: string;
  name: string;
  rollId: string;
  grade: string;
  disabilityCategory: string;
  guardianName: string;
  guardianPhone: string;
  therapist: string;
  iepStatus: 'ACTIVE' | 'PENDING_REVIEW' | 'COMPLETED';
}

const INITIAL_STUDENTS: Student[] = [
  { id: '1', name: 'Master Rahul Sharma', rollId: 'STU-2026-001', grade: 'Class 4-B', disabilityCategory: 'Autism Spectrum (ASD)', guardianName: 'Sunita Sharma', guardianPhone: '+91 98230 11223', therapist: 'Sarah Jenkins (SLP)', iepStatus: 'ACTIVE' },
  { id: '2', name: 'Miss Ananya Kulkarni', rollId: 'STU-2026-002', grade: 'Class 2-A', disabilityCategory: 'Speech & Articulation Impairment', guardianName: 'Vikram Kulkarni', guardianPhone: '+91 98211 44556', therapist: 'Michael Chen (OT)', iepStatus: 'ACTIVE' },
  { id: '3', name: 'Master Aarav Patel', rollId: 'STU-2026-003', grade: 'Class 5-C', disabilityCategory: 'Cerebral Palsy (Motor Skills)', guardianName: 'Rajesh Patel', guardianPhone: '+91 98900 77889', therapist: 'Priya Nair (PT)', iepStatus: 'PENDING_REVIEW' },
];

export const TenantAdminDashboard: React.FC = () => {
  const [students, setStudents] = useState<Student[]>(INITIAL_STUDENTS);
  const [activeTab, setActiveTab] = useState<'roster' | 'add_single' | 'bulk_upload'>('roster');
  const [searchTerm, setSearchTerm] = useState('');

  // Single Add State
  const [singleName, setSingleName] = useState('');
  const [singleRollId, setSingleRollId] = useState('');
  const [singleGrade, setSingleGrade] = useState('Class 3-A');
  const [singleCategory, setSingleCategory] = useState('Autism Spectrum (ASD)');
  const [singleGuardianName, setSingleGuardianName] = useState('');
  const [singleGuardianPhone, setSingleGuardianPhone] = useState('');
  const [singleTherapist, setSingleTherapist] = useState('Sarah Jenkins (SLP)');

  // Bulk Upload State
  const [file, setFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [parsedPreview, setParsedPreview] = useState<any[]>([]);

  const handleAddSingleStudent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!singleName || !singleGuardianName) return;

    const newStudent: Student = {
      id: Date.now().toString(),
      name: singleName,
      rollId: singleRollId || `STU-2026-00${students.length + 1}`,
      grade: singleGrade,
      disabilityCategory: singleCategory,
      guardianName: singleGuardianName,
      guardianPhone: singleGuardianPhone || '+91 98000 00000',
      therapist: singleTherapist,
      iepStatus: 'ACTIVE'
    };

    setStudents([newStudent, ...students]);
    setSingleName('');
    setSingleRollId('');
    setSingleGuardianName('');
    setSingleGuardianPhone('');
    setActiveTab('roster');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      setFile(selected);

      // Simulate parsing Excel / CSV rows
      const mockParsed = [
        { name: 'Miss Diya Sengupta', rollId: 'STU-2026-101', grade: 'Class 1-A', category: 'Hearing Impairment', guardian: 'Rohan Sengupta', phone: '+91 98450 11990', therapist: 'Sarah Jenkins (SLP)' },
        { name: 'Master Ishan Verma', rollId: 'STU-2026-102', grade: 'Class 3-B', category: 'Down Syndrome', guardian: 'Meera Verma', phone: '+91 98765 43210', therapist: 'Michael Chen (OT)' },
        { name: 'Master Tanmay Roy', rollId: 'STU-2026-103', grade: 'Class 6-A', category: 'Visual Impairment (Braille)', guardian: 'Kunal Roy', phone: '+91 98112 23344', therapist: 'Priya Nair (PT)' },
      ];
      setParsedPreview(mockParsed);
    }
  };

  const handleConfirmBulkUpload = () => {
    if (parsedPreview.length === 0) return;

    const uploadedStudents: Student[] = parsedPreview.map((item, idx) => ({
      id: (Date.now() + idx).toString(),
      name: item.name,
      rollId: item.rollId,
      grade: item.grade,
      disabilityCategory: item.category,
      guardianName: item.guardian,
      guardianPhone: item.phone,
      therapist: item.therapist,
      iepStatus: 'ACTIVE'
    }));

    setStudents([...uploadedStudents, ...students]);
    setUploadSuccess(`Successfully imported ${parsedPreview.length} students from "${file?.name}"!`);
    setFile(null);
    setParsedPreview([]);
    setActiveTab('roster');

    setTimeout(() => setUploadSuccess(''), 4000);
  };

  const downloadSampleTemplate = () => {
    const csvContent = "Student Name,Roll ID,Grade,Disability Category,Guardian Name,Guardian Phone,Primary Therapist\n" +
                       "Master Rahul Sharma,STU-2026-001,Class 4-B,Autism Spectrum (ASD),Sunita Sharma,+91 98230 11223,Sarah Jenkins (SLP)\n" +
                       "Miss Ananya Kulkarni,STU-2026-002,Class 2-A,Speech & Articulation Impairment,Vikram Kulkarni,+91 98211 44556,Michael Chen (OT)";
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'abilityos_student_import_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredStudents = students.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.rollId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.disabilityCategory.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      
      {/* Header & Tabs */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full uppercase tracking-wider mb-2">
              <Users className="w-3.5 h-3.5" /> Tenant School Admin Workspace
            </div>
            <h2 className="text-2xl font-black text-slate-900">Student Enrolment & Record Management</h2>
            <p className="text-xs text-slate-500">Manage individual student IEP profiles and bulk import via Excel/CSV</p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab('roster')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition ${
                activeTab === 'roster' 
                  ? 'bg-emerald-600 text-white shadow' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Student Roster ({students.length})
            </button>
            <button
              onClick={() => setActiveTab('add_single')}
              className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition ${
                activeTab === 'add_single' 
                  ? 'bg-emerald-600 text-white shadow' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              <UserPlus className="w-4 h-4" />
              Add Student
            </button>
            <button
              onClick={() => setActiveTab('bulk_upload')}
              className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition ${
                activeTab === 'bulk_upload' 
                  ? 'bg-blue-600 text-white shadow' 
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
              }`}
            >
              <FileSpreadsheet className="w-4 h-4" />
              Bulk Excel Upload
            </button>
          </div>
        </div>
      </div>

      {uploadSuccess && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs font-bold flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          {uploadSuccess}
        </div>
      )}

      {/* TAB 1: Student Roster View */}
      {activeTab === 'roster' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by student name, Roll ID, or category..."
                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>
            <button
              onClick={downloadSampleTemplate}
              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs flex items-center gap-1.5 border"
            >
              <Download className="w-4 h-4" />
              Download Excel Format Template
            </button>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b text-slate-600 font-bold uppercase tracking-wider">
                <tr>
                  <th className="p-3">Roll ID</th>
                  <th className="p-3">Student Name</th>
                  <th className="p-3">Class / Grade</th>
                  <th className="p-3">Disability Category</th>
                  <th className="p-3">Guardian Contact</th>
                  <th className="p-3">Assigned Therapist</th>
                  <th className="p-3">IEP Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredStudents.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-slate-500 font-bold">{s.rollId}</td>
                    <td className="p-3 font-bold text-slate-900">{s.name}</td>
                    <td className="p-3 text-slate-600">{s.grade}</td>
                    <td className="p-3">
                      <span className="px-2.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-full border border-blue-200">
                        {s.disabilityCategory}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="font-bold text-slate-800">{s.guardianName}</div>
                      <div className="text-[10px] text-slate-500">{s.guardianPhone}</div>
                    </td>
                    <td className="p-3 text-slate-700 font-medium">{s.therapist}</td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-extrabold rounded">
                        {s.iepStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: Add Single Student */}
      {activeTab === 'add_single' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm max-w-2xl mx-auto space-y-6">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 border-b pb-3">
            <UserPlus className="w-5 h-5 text-emerald-600" />
            Enrol Single Student
          </h3>

          <form onSubmit={handleAddSingleStudent} className="space-y-4 text-xs">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Student Full Name *</label>
                <input
                  type="text"
                  required
                  value={singleName}
                  onChange={(e) => setSingleName(e.target.value)}
                  placeholder="Master Rohan Verma"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Roll / Student ID</label>
                <input
                  type="text"
                  value={singleRollId}
                  onChange={(e) => setSingleRollId(e.target.value)}
                  placeholder="e.g. STU-2026-004"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Class / Grade</label>
                <input
                  type="text"
                  value={singleGrade}
                  onChange={(e) => setSingleGrade(e.target.value)}
                  placeholder="Class 3-A"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Disability Category</label>
                <select
                  value={singleCategory}
                  onChange={(e) => setSingleCategory(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white"
                >
                  <option value="Autism Spectrum (ASD)">Autism Spectrum (ASD)</option>
                  <option value="Speech & Articulation Impairment">Speech & Articulation Impairment</option>
                  <option value="Cerebral Palsy (Motor Skills)">Cerebral Palsy (Motor Skills)</option>
                  <option value="Hearing Impairment (ISL)">Hearing Impairment (ISL)</option>
                  <option value="Visual Impairment (Braille)">Visual Impairment (Braille)</option>
                  <option value="Down Syndrome">Down Syndrome</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Parent / Guardian Name *</label>
                <input
                  type="text"
                  required
                  value={singleGuardianName}
                  onChange={(e) => setSingleGuardianName(e.target.value)}
                  placeholder="Kavita Verma"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Guardian Phone Number</label>
                <input
                  type="text"
                  value={singleGuardianPhone}
                  onChange={(e) => setSingleGuardianPhone(e.target.value)}
                  placeholder="+91 98111 22334"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block font-bold text-slate-700 mb-1">Assigned Primary Therapist</label>
              <input
                type="text"
                value={singleTherapist}
                onChange={(e) => setSingleTherapist(e.target.value)}
                placeholder="Sarah Jenkins (SLP)"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <button
                type="button"
                onClick={() => setActiveTab('roster')}
                className="px-4 py-2 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow"
              >
                Save & Enrol Student
              </button>
            </div>
          </form>
        </div>
      )}

      {/* TAB 3: Bulk Excel Upload */}
      {activeTab === 'bulk_upload' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm max-w-3xl mx-auto space-y-6">
          <div className="flex items-center justify-between border-b pb-3">
            <div>
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                Bulk Student Import via Excel / CSV
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">Upload student rosters in batch using standard formatted spreadsheets</p>
            </div>

            <button
              onClick={downloadSampleTemplate}
              className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 text-xs font-bold rounded-lg flex items-center gap-1.5"
            >
              <Download className="w-4 h-4" />
              Download Template (.CSV)
            </button>
          </div>

          {/* Upload Drop Zone */}
          <div className="border-2 border-dashed border-blue-300 bg-blue-50/50 p-8 rounded-2xl text-center space-y-3">
            <Upload className="w-10 h-10 text-blue-600 mx-auto" />
            <div>
              <h4 className="text-sm font-bold text-slate-800">Choose Excel (.xlsx) or CSV (.csv) File</h4>
              <p className="text-xs text-slate-500 mt-1">Columns required: Student Name, Roll ID, Grade, Disability Category, Guardian Name, Guardian Phone</p>
            </div>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
            />
          </div>

          {/* Parsed Preview Table */}
          {parsedPreview.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-700">
                  Previewing {parsedPreview.length} Records Found in File:
                </span>
                <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded">
                  Format Validation: PASS
                </span>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200 max-h-48 overflow-y-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100 font-bold text-slate-700">
                    <tr>
                      <th className="p-2">Roll ID</th>
                      <th className="p-2">Student Name</th>
                      <th className="p-2">Grade</th>
                      <th className="p-2">Category</th>
                      <th className="p-2">Guardian</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {parsedPreview.map((p, idx) => (
                      <tr key={idx} className="bg-white">
                        <td className="p-2 font-mono">{p.rollId}</td>
                        <td className="p-2 font-bold text-slate-900">{p.name}</td>
                        <td className="p-2">{p.grade}</td>
                        <td className="p-2 text-blue-700">{p.category}</td>
                        <td className="p-2">{p.guardian}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => { setFile(null); setParsedPreview([]); }}
                  className="px-4 py-2 border border-slate-300 text-slate-700 font-bold rounded-lg text-xs hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmBulkUpload}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow flex items-center gap-1.5"
                >
                  <CheckCircle className="w-4 h-4" />
                  Confirm & Import All Students
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
